import type { MouseEvent } from "react"
import { useMemo } from "react"
import {
	IconChevronDownOutline18 as ChevronDown,
	IconEyeSlashOutline18 as EyeOff,
	IconTriangleWarningOutline18 as Warning,
} from "nucleo-ui-outline-18"

import type { RequestSource } from "@productlane/lib/src/portal-visibility"
import { cn } from "@productlane/lib/src/browser"
import {
	shownPortalIds,
	suggestedPortals,
	summarizePortals,
} from "@productlane/lib/src/portal-visibility"
import {
	Avatar,
	AvatarStack,
	Button,
	Checkbox,
	DropdownMenu,
	DropdownMenuItem,
	DropdownMenuSeparator,
	Switch,
	Tooltip,
} from "@productlane/ui"

import type { PortalEntry } from "@/lib/hooks/use-portal-entries"
import type { VisibilityEntityType } from "@/lib/hooks/useVisibilityActions"
import {
	getVisibilityLabels,
	getVisibilityOptions,
} from "@/components/shared/visibility-labels"
import {
	VISIBILITY_OPTION_ROW_CLASS,
	VisibilityOptionRow,
} from "@/components/shared/visibility-option-row"
import { toast } from "@/components/toast"
import { usePortalEntries } from "@/lib/hooks/use-portal-entries"
import { useVisibilityActions } from "@/lib/hooks/useVisibilityActions"

const VisibilityDot = ({ isChecked }: { isChecked: boolean }) => (
	<span
		className={cn(
			"h-1.5 w-1.5 flex-none rounded-full transition-colors duration-150 ease-out",
			isChecked ? "bg-green-500" : "bg-gray-300 dark:bg-gray-600",
		)}
	/>
)

const stopRowClick = (e: MouseEvent) => {
	e.preventDefault()
	e.stopPropagation()
}

/** One cell per portal in a list row; the header uses the same width. */
export const PORTAL_COLUMN_CLASS = "flex w-9 flex-none justify-center"

export const PortalAvatar = ({
	entry,
	dim,
}: {
	entry: Pick<PortalEntry, "name" | "logoUrl" | "colorSeed">
	dim?: boolean
}) => (
	<Avatar
		name={entry.name}
		image={entry.logoUrl}
		colorSeed={entry.colorSeed}
		roundness="sm"
		size="xs"
		className={cn("flex-none", dim && "opacity-30 grayscale")}
	/>
)

/** Portal avatars over the switch columns of the feature requests list. */
export const PortalColumnsHeader = ({ className }: { className?: string }) => {
	const { entries } = usePortalEntries()
	if (entries.length === 0) return null
	return (
		<div className={cn("flex justify-end py-1.5 pr-3", className)}>
			{entries.map((entry) => (
				<Tooltip key={entry.id} label={entry.name} side="top">
					<span className={PORTAL_COLUMN_CLASS}>
						<PortalAvatar entry={entry} />
					</span>
				</Tooltip>
			))}
		</div>
	)
}

interface VisibilityToggleProps {
	type: VisibilityEntityType
	id: string
	isVisible?: boolean | null
	portalInstanceIds?: readonly string[] | null
	/**
	 * `switch` (default): for list rows. With several portals, one switch per
	 * portal, so the state never depends on a portal picked somewhere else.
	 * `button`: for detail top bars. With several portals, a checklist.
	 */
	variant?: "switch" | "button"
	/** The item's threads, to suggest the portals customers asked on. */
	requests?: readonly RequestSource[]
}

export const VisibilityToggle = ({
	type,
	id,
	isVisible,
	portalInstanceIds,
	variant = "switch",
	requests,
}: VisibilityToggleProps) => {
	const { canEdit, updateVisibility, setPortalVisibility } =
		useVisibilityActions()
	const { entries, namedPortalIds, fixBlocker } = usePortalEntries()
	const multiPortal = entries.length > 0

	const current = useMemo(
		() => ({
			isVisible: isVisible ?? null,
			portalInstanceIds: portalInstanceIds ?? [],
		}),
		[isVisible, portalInstanceIds],
	)
	const shown = useMemo(() => new Set(shownPortalIds(current)), [current])

	const togglePortal = (portalId: string, value: boolean) =>
		setPortalVisibility({
			id,
			type,
			portalId,
			value,
			namedPortalIds,
			current,
		})

	const setVisible = (value: boolean) => updateVisibility({ id, type, value })

	if (!multiPortal) {
		const isChecked = isVisible === true
		const labels = getVisibilityLabels(isChecked)
		if (variant === "switch") {
			return (
				<Tooltip label={labels.tooltip} side="left">
					<div role="presentation" onClick={stopRowClick}>
						<Switch
							name={`visibility-${id}`}
							checked={isChecked}
							onCheckedChange={(value) => void setVisible(value)}
							disabled={!canEdit}
							size="small"
						/>
					</div>
				</Tooltip>
			)
		}
		return (
			<div role="presentation" onClick={stopRowClick}>
				<DropdownMenu
					trigger={
						<Button
							variant="outline"
							wrapperClassName="gap-1.5"
							iconRight={ChevronDown}
							disabled={!canEdit}
						>
							<VisibilityDot isChecked={isChecked} />
							{labels.button}
						</Button>
					}
					contentProps={{ side: "bottom", align: "end" }}
					className="w-72"
				>
					{getVisibilityOptions(false).map((option) => (
						<DropdownMenuItem
							key={option.label}
							className={VISIBILITY_OPTION_ROW_CLASS}
							onSelect={() => {
								if (option.value === isChecked) return
								void setVisible(option.value)
							}}
						>
							<VisibilityOptionRow
								mark={<VisibilityDot isChecked={option.value} />}
								label={option.label}
								description={option.description}
								isSelected={option.value === isChecked}
							/>
						</DropdownMenuItem>
					))}
				</DropdownMenu>
			</div>
		)
	}

	if (variant === "switch") {
		return (
			<div role="presentation" className="flex" onClick={stopRowClick}>
				{entries.map((entry) => (
					<Tooltip key={entry.id} label={entry.name} side="top">
						<span className={PORTAL_COLUMN_CLASS}>
							<Switch
								name={`visibility-${id}-${entry.id}`}
								aria-label={entry.name}
								checked={shown.has(entry.id)}
								onCheckedChange={(value) => void togglePortal(entry.id, value)}
								disabled={!canEdit}
								size="small"
							/>
						</span>
					</Tooltip>
				))}
			</div>
		)
	}

	return (
		<PortalChecklist
			entries={entries}
			shown={shown}
			suggestions={suggestedPortals(current, requests ?? [], namedPortalIds)}
			canEdit={canEdit}
			onToggle={(portalId, value) => void togglePortal(portalId, value)}
			onHideEverywhere={() => void setVisible(false)}
			onFixBlocker={(portalId) => {
				fixBlocker(portalId).catch((error: unknown) => {
					toast.error({
						title: "Couldn't turn on feature requests",
						description: error instanceof Error ? error.message : undefined,
					})
				})
			}}
		/>
	)
}

interface PortalChecklistProps {
	entries: PortalEntry[]
	shown: ReadonlySet<string>
	suggestions: { portalId: string; count: number }[]
	canEdit: boolean
	onToggle: (portalId: string, value: boolean) => void
	onHideEverywhere: () => void
	onFixBlocker: (portalId: string) => void
}

export const PortalChecklist = ({
	entries,
	shown,
	suggestions,
	canEdit,
	onToggle,
	onHideEverywhere,
	onFixBlocker,
}: PortalChecklistProps) => {
	const shownIds = entries.filter((e) => shown.has(e.id)).map((e) => e.id)
	const requestCount = new Map(suggestions.map((s) => [s.portalId, s.count]))

	return (
		<div role="presentation" onClick={stopRowClick}>
			<DropdownMenu
				trigger={
					<Button
						variant="outline"
						wrapperClassName="gap-1.5"
						iconRight={ChevronDown}
						Icon={shownIds.length === 0 ? EyeOff : undefined}
						disabled={!canEdit}
					>
						{shownIds.length > 0 && (
							<AvatarStack
								size="xxs"
								roundness="sm"
								avatars={entries
									.filter((e) => shown.has(e.id))
									.map((e) => ({
										name: e.name,
										image: e.logoUrl,
										colorSeed: e.colorSeed,
									}))}
							/>
						)}
						{summarizePortals(shownIds, entries)}
						{suggestions.length > 0 && (
							<span
								aria-label="Requested on a portal it's not shown on"
								className="h-1.5 w-1.5 flex-none rounded-full bg-blue-500"
							/>
						)}
					</Button>
				}
				contentProps={{ side: "bottom", align: "end" }}
				className="w-72"
			>
				<div className="px-2 pb-1 pt-1.5 text-xs font-medium text-secondary">
					Show on
				</div>
				{entries.map((entry) => {
					const checked = shown.has(entry.id)
					const requests = requestCount.get(entry.id)
					return (
						<div key={entry.id}>
							<DropdownMenuItem
								className="gap-2"
								onSelect={(e) => {
									// Keep the menu open so several portals can be set in one go.
									e.preventDefault()
									onToggle(entry.id, !checked)
								}}
							>
								<Checkbox size="sm" checked={checked} tabIndex={-1} />
								<PortalAvatar entry={entry} />
								<span className="min-w-0 flex-1 truncate">{entry.name}</span>
								{requests ? (
									<span className="flex-none text-xs font-normal text-blue-600 dark:text-blue-400">
										{requests === 1 ? "1 request" : `${requests} requests`}
									</span>
								) : null}
								{checked && entry.blocker && (
									<Warning className="h-3.5 w-3.5 flex-none text-amber-500" />
								)}
							</DropdownMenuItem>
							{checked && entry.blocker && (
								<div className="flex items-center gap-1 pb-1.5 pl-[3.25rem] pr-2 text-xxs text-amber-600 dark:text-amber-400">
									<span className="min-w-0 truncate">{entry.blocker}.</span>
									<button
										type="button"
										className="flex-none underline"
										onClick={(e) => {
											e.stopPropagation()
											onFixBlocker(entry.id)
										}}
									>
										Turn on
									</button>
								</div>
							)}
						</div>
					)
				})}
				<DropdownMenuSeparator />
				<DropdownMenuItem
					Icon={EyeOff}
					disabled={shownIds.length === 0}
					className="font-normal text-secondary"
					onSelect={onHideEverywhere}
				>
					Hide everywhere
				</DropdownMenuItem>
			</DropdownMenu>
		</div>
	)
}
